/**
 * SDF images for the square / triangle point symbols (MapLibre has no built-in shape
 * icons, so the properties panel registers these through map.addImage(..., { sdf: true })
 * from components/map/PointSymbolImages.js). Pure: returns RGBA pixels, no canvas.
 *
 * The image is SIZE px square; the shape is 16px wide, centred, with an 8px signed
 * distance buffer around it. Alpha encodes the distance (192 at the edge), so
 * `icon-color` tints it and `icon-halo-*` draws the outline.
 */
export const SYMBOL_IMAGE_SIZE = 32;
const HALF = 8;      // shape half-extent (px)
const BUFFER = 8;    // SDF falloff (px)

const SHAPES = {
  square: [[-HALF, -HALF], [HALF, -HALF], [HALF, HALF], [-HALF, HALF]],
  triangle: [[0, -HALF], [HALF, HALF * 0.9], [-HALF, HALF * 0.9]],
};

function segDist(px, py, [ax, ay], [bx, by]) {
  const dx = bx - ax, dy = by - ay;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

function inside(px, py, pts) {
  let c = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i], [xj, yj] = pts[j];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
}

/** Signed distance (px) from (x, y) to the shape's edge: negative inside. */
export function shapeDistance(shape, x, y) {
  const pts = SHAPES[shape];
  if (!pts) return Math.hypot(x, y) - HALF; // circle
  let d = Infinity;
  for (let i = 0; i < pts.length; i++) d = Math.min(d, segDist(x, y, pts[i], pts[(i + 1) % pts.length]));
  return inside(x, y, pts) ? -d : d;
}

/** { width, height, data } RGBA pixels of the SDF image for 'circle' | 'square' | 'triangle'. */
export function symbolPixels(shape, size = SYMBOL_IMAGE_SIZE) {
  const data = new Uint8ClampedArray(size * size * 4);
  const c = size / 2;
  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      const sd = shapeDistance(shape, px + 0.5 - c, py + 0.5 - c);
      const a = Math.max(0, Math.min(255, Math.round(192 - (sd * 192) / BUFFER)));
      const o = (py * size + px) * 4;
      data[o] = data[o + 1] = data[o + 2] = 255;
      data[o + 3] = a;
    }
  }
  return { width: size, height: size, data };
}
